import { useState, useEffect } from 'react';
import { Link, useLocation } from 'react-router-dom';

const STORAGE_KEY = 'max_sourcing_cookie_consent';

export default function CookieBanner() {
  const [visible, setVisible] = useState(false);
  const [leaving, setLeaving] = useState(false);
  const location = useLocation();
  const isFrench = location.pathname.startsWith('/fr');

  useEffect(() => {
    const consent = localStorage.getItem(STORAGE_KEY);
    if (!consent) {
      // Small delay so it doesn't flash immediately on load
      const t = setTimeout(() => setVisible(true), 1200);
      return () => clearTimeout(t);
    }
  }, []);

  const dismiss = (accepted: boolean) => {
    localStorage.setItem(STORAGE_KEY, accepted ? 'accepted' : 'declined');
    setLeaving(true);
    setTimeout(() => setVisible(false), 400);
  };

  if (!visible) return null;

  return (
    <div
      className={`fixed bottom-0 left-0 right-0 z-[9999] transition-transform duration-400 ${
        leaving ? 'translate-y-full' : 'translate-y-0'
      }`}
    >
      {/* Backdrop blur strip */}
      <div className="w-full bg-stone-900/95 backdrop-blur-sm border-t border-stone-700 px-6 md:px-16 py-5">
        <div className="max-w-7xl mx-auto flex flex-col md:flex-row items-start md:items-center gap-5 justify-between">

          {/* Icon + text */}
          <div className="flex items-start gap-4 flex-1">
            <div className="w-9 h-9 flex items-center justify-center rounded-md bg-emerald-700 shrink-0 mt-0.5">
              <i className="ri-shield-check-line text-white text-lg" />
            </div>
            <div>
              <p className="text-white font-semibold text-sm mb-1">
                {isFrench ? 'Nous utilisons des témoins (cookies)' : 'We use cookies'}
              </p>
              <p className="text-stone-400 text-xs leading-relaxed max-w-2xl">
                {isFrench
                  ? `Ce site utilise des témoins essentiels et analytiques pour améliorer votre expérience. Aucun témoin publicitaire ou de suivi n'est utilisé. En continuant, vous acceptez notre utilisation des témoins conformément à nos `
                  : `This site uses essential and analytics cookies to improve your experience. No advertising or tracking cookies are used. By continuing, you agree to our use of cookies as described in our `}
                <Link
                  to={isFrench ? '/fr/conditions' : '/terms'}
                  className="text-emerald-400 hover:text-emerald-300 underline cursor-pointer"
                >
                  {isFrench ? 'Conditions d\'utilisation' : 'Terms of Use'}
                </Link>.
              </p>
            </div>
          </div>

          {/* Buttons */}
          <div className="flex items-center gap-3 shrink-0">
            <button
              onClick={() => dismiss(false)}
              className="text-xs font-semibold text-stone-400 hover:text-white transition-colors cursor-pointer whitespace-nowrap px-4 py-2 rounded-md border border-stone-600 hover:border-stone-400"
            >
              {isFrench ? 'Refuser' : 'Decline'}
            </button>
            <button
              onClick={() => dismiss(true)}
              className="text-xs font-bold bg-emerald-600 hover:bg-emerald-500 text-white px-5 py-2 rounded-md transition-colors cursor-pointer whitespace-nowrap"
            >
              {isFrench ? 'Accepter' : 'Accept All'}
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}
